import axiosInstance from 'src/config/axios.config';
import axios, { isAxiosError } from 'axios';

import { getUserIdFromToken } from 'src/utils/jwt';
import { storage } from 'src/utils/storage';

import { API_ENDPOINTS } from 'src/config/api.config';

export enum BranchStatus {
  OPEN = 'OPEN',      // value: 0
  CLOSED = 'CLOSED',  // value: 1
  MAINTENANCE = 'MAINTENANCE'  // value: 2
}

// Haftalık çalışma saati (gün bazlı). dayOfWeek: 1=Pazartesi .. 7=Pazar (ISO).
export interface BranchWorkingHour {
  dayOfWeek: number; // 1=Pzt .. 7=Paz (ISO)
  closed: boolean;
  openTime: string | null;  // "HH:mm"
  closeTime: string | null; // "HH:mm"
}

// Vardiya kapsamı: SHARED=Paylaşılan, PERSONAL=Kişisel.
export type ShiftScope = 'SHARED' | 'PERSONAL';

// Vardiya (shift) - bir şubeye ait, haftalık çalışma saatleri içeren tanım.
export interface ShiftDto {
  id: string;
  branchId: string;
  name: string;
  scope: ShiftScope;
  isDefault: boolean;
  workingHours: BranchWorkingHour[];
}

// Vardiya oluşturma/güncelleme gövdesi.
export interface ShiftUpsertDto {
  name: string;
  scope: ShiftScope;
  workingHours: BranchWorkingHour[];
}

export interface BranchDto {
  id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  branchOpeningTime?: string | null;
  branchClosingTime?: string | null;
  workingHours?: BranchWorkingHour[]; // Backend her zaman 7 kayıt döner (çözümlenmiş)
  dailyEmployeeCount?: number | null; // readonly
  averageDailyAttendance?: number | null; // readonly
  status?: BranchStatus | null;
  responsibleUsernames?: string[] | null; // Backend'den null gelebilir
  companyId?: string;
  companyName?: string;
  address?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBranchDto {
  name: string;
  latitude: number;
  longitude: number;
  branchOpeningTime: string;
  branchClosingTime: string;
  status: BranchStatus;
  responsibleUsernames: string[];
  companyId?: string;
  address?: string;
  workingHours?: BranchWorkingHour[]; // 7 günün tamamı gönderilir (opsiyonel)
}

export interface UpdateBranchDto extends Partial<CreateBranchDto> {
  // dailyEmployeeCount ve averageDailyAttendance readonly - güncellenmez
}

// Mock data for branches when backend is not available
const originalMockBranches: BranchDto[] = [
  {
    id: "branch-1",
    name: "Merkez Şube",
    latitude: 41.0082,
    longitude: 28.9784,
    branchOpeningTime: "08:00",
    branchClosingTime: "18:00",
    dailyEmployeeCount: 25,
    averageDailyAttendance: 22,
    status: BranchStatus.OPEN,
    responsibleUsernames: ["manager1@firma.com", "admin1@firma.com"],
    companyId: "company-1",
    address: "İstanbul Merkez Mah. No:1",
    createdAt: "2024-01-15T08:00:00Z",
    updatedAt: "2024-06-08T12:00:00Z"
  },
  {
    id: "branch-2", 
    name: "Kadıköy Şubesi",
    latitude: 40.9904,
    longitude: 29.0206,
    branchOpeningTime: "09:00",
    branchClosingTime: "19:00",
    dailyEmployeeCount: 18,
    averageDailyAttendance: 16,
    status: BranchStatus.OPEN,
    responsibleUsernames: ["manager2@firma.com"],
    companyId: "company-1",
    address: "Kadıköy Moda Cad. No:45",
    createdAt: "2024-02-20T10:00:00Z",
    updatedAt: "2024-06-07T14:30:00Z"
  },
  {
    id: "branch-3",
    name: "Beşiktaş Ofis",
    latitude: 41.0424,
    longitude: 29.0089,
    branchOpeningTime: "08:30",
    branchClosingTime: "17:30",
    dailyEmployeeCount: 12,
    averageDailyAttendance: 11,
    status: BranchStatus.MAINTENANCE,
    responsibleUsernames: ["manager3@firma.com", "tech1@firma.com"],
    companyId: "company-2",
    address: "Beşiktaş İş Merkezi Kat:5",
    createdAt: "2024-03-10T09:15:00Z",
    updatedAt: "2024-06-08T11:45:00Z"
  }
];

// Stateful mock data management with localStorage persistence
const STORAGE_KEY_DELETED_BRANCHES = 'qr-admin-deleted-branches';
const STORAGE_KEY_MOCK_BRANCHES = 'qr-admin-mock-branches';

// Reset localStorage if corrupted
const resetLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY_DELETED_BRANCHES);
    localStorage.removeItem(STORAGE_KEY_MOCK_BRANCHES);
  } catch (error) {
    console.log('resetLocalStorage error', error);  
  }
};

// Load state from localStorage
const loadDeletedBranchIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_DELETED_BRANCHES);
    if (!stored) return new Set();
    
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      resetLocalStorage();
      return new Set();
    }
    
    return new Set(parsed);
  } catch (error) {
    resetLocalStorage();
    return new Set();
  }
};

const loadMockBranches = (): BranchDto[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MOCK_BRANCHES);
    if (!stored) return [...originalMockBranches];
    
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...originalMockBranches];
    }
    
    return parsed;
  } catch (error) {
    console.log('loadMockBranches error', error);
    return [...originalMockBranches];
  }
};

// eslint-disable-next-line prefer-const
let mockBranches: BranchDto[] = loadMockBranches();
// eslint-disable-next-line prefer-const
let deletedBranchIds: Set<string> = loadDeletedBranchIds();

// Save state to localStorage
const saveDeletedBranchIds = () => {
  localStorage.setItem(STORAGE_KEY_DELETED_BRANCHES, JSON.stringify(Array.from(deletedBranchIds)));
};

const saveMockBranches = () => {
  localStorage.setItem(STORAGE_KEY_MOCK_BRANCHES, JSON.stringify(mockBranches));
};

// Helper function to get filtered mock branches (excluding deleted ones)
const getActiveMockBranches = (): BranchDto[] => 
  mockBranches.filter(branch => !deletedBranchIds.has(branch.id));

// PageResponse format to match frontend expectations
interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Tüm şubeleri getir
export const getAllBranches = async (page: number = 0, size: number = 10, sortBy: string = 'id', sortDirection: string = 'asc'): Promise<BranchDto[]> => {
  const userId = getUserIdFromToken();
  if (!userId) {
    throw new Error('User ID not found in token');
  }
  // Try to get from backend first
  try {
    const response = await axiosInstance.get<BranchDto[]>(`${API_ENDPOINTS.BRANCHES}`);
    // If backend returns array, use it directly
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data;
    }
    // If backend returns empty array, fallback to mock
    const activeMockBranches = getActiveMockBranches();
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedData = activeMockBranches.slice(startIndex, endIndex);
    return paginatedData;
  } catch (backendError) {
    // Return mock data with proper pagination
    const activeMockBranches = getActiveMockBranches();
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedData = activeMockBranches.slice(startIndex, endIndex);
    return paginatedData;
  }
};

// Şube detaylarını getir
export const getBranchById = async (id: string): Promise<BranchDto> => {
  try {
    const response = await axiosInstance.get<BranchDto>(`${API_ENDPOINTS.BRANCHES}/${id}`);
    return response.data;
  } catch (error) {
    // Backend CEVAP verdiyse (4xx/5xx) gerçek hatayı yüzeye çıkar. Mock'a düşmek
    // gerçek backend hatasını "Branch with id ... not found" yalanıyla maskeliyordu.
    if (isAxiosError(error) && error.response) {
      throw error;
    }
    // Sadece backend'e hiç ulaşılamadığında (offline/dev) mock fallback.
    const mockBranch = getActiveMockBranches().find(branch => branch.id === id);
    if (!mockBranch) {
      throw new Error(`Branch with id ${id} not found`);
    }
    return mockBranch;
  }
};

// Şube güncelleme
export const updateBranch = async (id: string, data: UpdateBranchDto): Promise<BranchDto> => {
  try {
    const response = await axiosInstance.put<BranchDto>(`${API_ENDPOINTS.BRANCHES}/${id}`, data);
    return response.data;
  } catch (error) {
    // Backend CEVAP verdiyse (4xx/5xx) gerçek hatayı yüzeye çıkar; edit ekranı
    // err.response.data.message'ı gösterir. Mock'a düşüp "Branch with id ... not found"
    // uydurmak canlı arızada gerçek 500'ü (working-hours unique ihlali) gizledi.
    if (isAxiosError(error) && error.response) {
      throw error;
    }
    // Sadece backend'e hiç ulaşılamadığında (offline/dev) mock fallback.
    const mockBranch = getActiveMockBranches().find(branch => branch.id === id);
    if (!mockBranch) {
      throw new Error(`Branch with id ${id} not found`);
    }
    return { ...mockBranch, ...data };
  }
};

// Şube oluşturma
export const createBranch = async (data: CreateBranchDto): Promise<BranchDto> => {
  try {
    const response = await axiosInstance.post<BranchDto>(`${API_ENDPOINTS.BRANCHES}`, data);
    return response.data;
  } catch (error) {
    const newBranch: BranchDto = {
      id: `branch-${Date.now()}`,
      ...data,
      dailyEmployeeCount: 0,
      averageDailyAttendance: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Add to mock data for stateful behavior
    mockBranches.push(newBranch);
    saveMockBranches(); // Persist to localStorage
    
    return newBranch;
  }
};

// ===============================
// VARDİYA (SHIFT) API
// ===============================

// Bir şubenin tüm vardiyalarını getir.
export const getBranchShifts = async (branchId: string): Promise<ShiftDto[]> => {
  const response = await axiosInstance.get<ShiftDto[]>(
    `${API_ENDPOINTS.BRANCHES}/${branchId}/shifts`
  );
  return response.data;
};

// Yeni vardiya oluştur.
export const createShift = async (
  branchId: string,
  dto: ShiftUpsertDto
): Promise<ShiftDto> => {
  const response = await axiosInstance.post<ShiftDto>(
    `${API_ENDPOINTS.BRANCHES}/${branchId}/shifts`,
    dto
  );
  return response.data;
};

// Mevcut bir vardiyayı güncelle.
export const updateShift = async (
  branchId: string,
  shiftId: string,
  dto: ShiftUpsertDto
): Promise<ShiftDto> => {
  const response = await axiosInstance.put<ShiftDto>(
    `${API_ENDPOINTS.BRANCHES}/${branchId}/shifts/${shiftId}`,
    dto
  );
  return response.data;
};

// Bir vardiyayı sil.
export const deleteShift = async (branchId: string, shiftId: string): Promise<void> => {
  await axiosInstance.delete(`${API_ENDPOINTS.BRANCHES}/${branchId}/shifts/${shiftId}`);
};

// ----- Toplu atama (roster) -----

// Toplu vardiya atama gövdesi.
export interface RosterAssignDto {
  employeeIds: string[];
  shiftId: string;
  effectiveFrom: string; // "YYYY-MM-DD"
  effectiveTo?: string | null; // "YYYY-MM-DD" | null
}

// Toplu atama sonucu (çalışan bazında ok/errorRef).
export interface RosterResultDto {
  results: { employeeId: string; ok: boolean; errorRef?: string }[];
}

// Bir vardiyayı birden fazla çalışana toplu ata.
export const bulkAssignRoster = async (
  branchId: string,
  shiftId: string,
  dto: RosterAssignDto
): Promise<RosterResultDto> => {
  const response = await axiosInstance.post<RosterResultDto>(
    `${API_ENDPOINTS.BRANCHES}/${branchId}/shifts/${shiftId}/roster`,
    dto
  );
  return response.data;
};

// Varsayılan vardiyayı güncelle.
export const updateDefaultShift = async (
  branchId: string,
  dto: ShiftUpsertDto
): Promise<ShiftDto> => {
  const response = await axiosInstance.put<ShiftDto>(
    `${API_ENDPOINTS.BRANCHES}/${branchId}/default-shift`,
    dto
  );
  return response.data;
};

// Şube silinebilirlik kontrolü
export const checkBranchDeletable = async (id: string): Promise<{ canDelete: boolean; activeEmployeeCount: number; message: string }> => {
  try {
    // Branch'deki aktif employee sayısını kontrol et
    // Bu endpoint yoksa mock data döndürüyoruz
    const response = await axios.get(`${API_ENDPOINTS.BRANCHES}/${id}/employees/count`);
    const activeEmployeeCount = response.data.count || 0;
    
    if (activeEmployeeCount > 0) {
      return {
        canDelete: false,
        activeEmployeeCount,
        message: `Bu şubede ${activeEmployeeCount} aktif çalışan bulunmaktadır. Önce çalışanları başka şubeye transfer edin veya işten çıkarın.`
      };
    }
    
    return {
      canDelete: true,
      activeEmployeeCount: 0,
      message: 'Şube silinebilir.'
    };
  } catch (error) {
    return {
      canDelete: true,
      activeEmployeeCount: 0,
      message: 'Employee sayısı kontrol edilemedi, silme işlemi denenecek.'
    };
  }
};

// Şube silme
export const deleteBranch = async (id: string): Promise<void> => {
  try {
    await axios.delete(`${API_ENDPOINTS.BRANCHES}/${id}`);
  } catch (error: any) {
    // Extract employee count from error message if present
    const errorMessage = error.response?.data?.message || error.message || 'Bilinmeyen hata';
    
    // Check if it's the employee constraint error
    if (errorMessage.includes('Cannot delete branch with active employees')) {
      const employeeCountMatch = errorMessage.match(/Found (\d+) active employees/);
      const employeeCount = employeeCountMatch ? employeeCountMatch[1] : 'bazı';
      throw new Error(`Bu şube silinemez: ${employeeCount} aktif çalışan bulunmaktadır. Önce çalışanları başka şubeye transfer edin veya işten çıkarın.`);
    }
    
    // Re-throw other errors as is
    throw new Error(`Silme işlemi başarısız: ${errorMessage}`);
  }
};

// Tüm şubeler (sayfalama desteği ile)
export const getAllBranchesWithPagination = async (
  page = 0, 
  size = 5, 
  sortBy = 'name', 
  sortDirection = 'asc'
): Promise<PageResponse<BranchDto>> => {
  try {
    const userId = getUserIdFromToken();
    
    if (!userId) {
      throw new Error('User ID not found in token. Please log in again.');
    }

    const endpoint = `${API_ENDPOINTS.BRANCHES}/for-user/${userId}`;
    
    const response = await axiosInstance.get<PageResponse<BranchDto>>(endpoint, {
      params: {
        page,
        size,
        sortBy,
        sortDirection,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error in getAllBranchesWithPagination:', error);
    throw error;
  }
};

// Export interfaces ve fonksiyonları
export interface ExportBranchParams {
  format: 'PDF' | 'EXCEL';
  companyId?: string;
  branchId?: string;
  fileName?: string;
  exportType?: 'current' | 'company' | 'branch' | 'all';
}

// Manager/Admin için branch export
export const exportBranches = async (params: ExportBranchParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axiosInstance.get(
      `/api/export/branches?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Şube export işlemi sırasında bir hata oluştu');
  }
};

// Super Admin için tüm şubeleri export
export const exportAllBranches = async (params: ExportBranchParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axiosInstance.get(
      `/api/export/branches/all?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için Super Admin yetkisi gerekiyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Super Admin branch export işlemi sırasında bir hata oluştu');
  }
};

// File download helper for branches
export const downloadBranchFile = (blob: Blob, fileName: string, format: 'PDF' | 'EXCEL') => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Backend'den gelen dosya adını kullan, yoksa default oluştur
    let finalFileName = fileName;
    if (!fileName || !fileName.includes('.')) {
      const extension = format === 'PDF' ? '.pdf' : '.xlsx';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      finalFileName = `subeler-${timestamp}${extension}`;
    }
    
    link.download = finalFileName;
    
    // Dosyayı indir
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Memory cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
    
  } catch (error) {
    throw new Error('Dosya indirilemedi');
  }
};

// ===============================
// BRANCH STATISTICS API
// ===============================

// Branch Statistics Types
export interface BranchStatistics {
  totalEmployees: number;
  activeEmployees: number;
  monthlyAttendanceRate: number;
  averageDailyHours: number;
  todayActiveCount: number;
}

export interface AttendanceTrend {
  date: string;
  attendanceCount: number;
  attendanceRate: number;
}

export interface EmployeePerformance {
  employeeId: string;
  employeeName: string;
  totalHours: number;
  attendanceRate: number;
  averageHours: number;
}

// Branch Statistics API Functions
export const getBranchStatistics = async (branchId: string): Promise<BranchStatistics> => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.BRANCHES}/${branchId}/statistics`);
    return response.data;
  } catch (error) {
    // Fallback to mock data if backend not ready
    console.warn('Branch statistics API not available, using mock data');
    return {
      totalEmployees: 24,
      activeEmployees: 18,
      monthlyAttendanceRate: 89.5,
      averageDailyHours: 8.2,
      todayActiveCount: 16
    };
  }
};

export const getBranchAttendanceTrend = async (
  branchId: string, 
  days: number = 30
): Promise<AttendanceTrend[]> => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.BRANCHES}/${branchId}/attendance-trend`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    // Fallback to mock data
    console.warn('Branch attendance trend API not available, using mock data');
    const mockData: AttendanceTrend[] = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      mockData.push({
        date: date.toISOString().split('T')[0],
        attendanceCount: Math.floor(Math.random() * 8) + 16, // 16-24 arası
        attendanceRate: Math.floor(Math.random() * 20) + 80 // %80-100 arası
      });
    }
    return mockData;
  }
};

export const getBranchEmployeePerformance = async (
  branchId: string, 
  limit: number = 10
): Promise<EmployeePerformance[]> => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.BRANCHES}/${branchId}/employee-performance`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    // Fallback to mock data
    console.warn('Branch employee performance API not available, using mock data');
    const employees = [
      'Ahmet Yılmaz', 'Fatma Kaya', 'Mehmet Demir', 'Ayşe Şahin', 'Can Özkan',
      'Zeynep Arslan', 'Emre Koç', 'Selin Aydın', 'Burak Çelik', 'Merve Güler'
    ];
    
    return employees.slice(0, limit).map((name, index) => ({
      employeeId: `emp-${index + 1}`,
      employeeName: name,
      totalHours: Math.floor(Math.random() * 40) + 160, // 160-200 saat
      attendanceRate: Math.floor(Math.random() * 20) + 80, // %80-100
      averageHours: Math.floor(Math.random() * 3) + 7 // 7-10 saat
    }));
  }
};

// Branch Managers API - Updated to match backend model
export interface BranchManager {
  id: string;
  assignedAt: string;
  removedAt?: string;
  isActive: boolean;
  assignedBy?: string;
  removedBy?: string;
  notes?: string;
  // User details from backend relationship
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    profilePictureUrl?: string;
    roles: string[];
    companyName?: string;
    username?: string;
  };
  // Branch details from backend relationship  
  branch: {
    id: string;
    name: string;
  };
  // Assignment details for UI compatibility
  assignedByUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface BranchManagerCreateDto {
  userId: string;
  branchId: string;
  assignedBy: string;
}

export interface BranchManagersResponse {
  managers: BranchManager[];
  totalCount: number;
  activeCount: number;
  inactiveCount: number;
}

export interface AvailableManagersResponse {
  users: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    profilePictureUrl?: string;
    roles: string[];
    companyName?: string;
  }[];
}

// Search parameters for manager filtering (Görev 5)
export interface BranchManagerSearchParams {
  name?: string;
  isActive?: boolean;
}

/*
  ====================================================================
  GÖREV 4: BACKEND GELİŞTİRİCİSİ İÇİN TALİMATLAR
  ====================================================================
  
  Backend Geliştirmesi Hedefi: 
  GET /api/branches/{branchId}/managers endpoint'inin arama ve filtreleme
  parametrelerini kabul etmesini sağlamak.
  
  Mevcut Endpoint: GET /api/branches/{branchId}/managers
  Hedef Endpoint:  GET /api/branches/{branchId}/managers?name=Ahmet&isActive=true
  
  BACKEND ADIMLARI:
  
  1. BranchController Güncellemesi:
  ```java
  @GetMapping("/{branchId}/managers")
  public ResponseEntity<List<BranchManager>> getBranchManagers(
      @PathVariable String branchId,
      @RequestParam(required = false) String name,
      @RequestParam(required = false) Boolean isActive
  ) {
      List<BranchManager> managers = branchManagerService.getBranchManagers(
          branchId, name, isActive
      );
      return ResponseEntity.ok(managers);
  }
  ```
  
  2. BranchManagerService Güncellemesi:
  ```java
  public List<BranchManager> getBranchManagers(String branchId, String name, Boolean isActive) {
      Specification<BranchManager> spec = Specification.where(null);
      
      // Branch ID filtresi (zorunlu)
      spec = spec.and(BranchManagerSpecification.belongsToBranch(branchId));
      
      // İsim filtresi (opsiyonel)
      if (name != null && !name.trim().isEmpty()) {
          spec = spec.and(BranchManagerSpecification.userNameContains(name.trim()));
      }
      
      // Aktiflik durumu filtresi (opsiyonel) 
      if (isActive != null) {
          spec = spec.and(BranchManagerSpecification.isActive(isActive));
      }
      
      return branchManagerRepository.findAll(spec);
  }
  ```
  
  3. BranchManagerSpecification Sınıfı (Yeni):
  ```java
  @Component
  public class BranchManagerSpecification {
      
      public static Specification<BranchManager> belongsToBranch(String branchId) {
          return (root, query, criteriaBuilder) -> 
              criteriaBuilder.equal(root.get("branch").get("id"), branchId);
      }
      
      public static Specification<BranchManager> userNameContains(String name) {
          return (root, query, criteriaBuilder) -> {
              Join<BranchManager, User> userJoin = root.join("user");
              
              String searchPattern = "%" + name.toLowerCase() + "%";
              
              return criteriaBuilder.or(
                  criteriaBuilder.like(
                      criteriaBuilder.lower(userJoin.get("firstName")), 
                      searchPattern
                  ),
                  criteriaBuilder.like(
                      criteriaBuilder.lower(userJoin.get("lastName")), 
                      searchPattern
                  ),
                  criteriaBuilder.like(
                      criteriaBuilder.lower(userJoin.get("email")), 
                      searchPattern
                  )
              );
          };
      }
      
      public static Specification<BranchManager> isActive(Boolean isActive) {
          return (root, query, criteriaBuilder) -> 
              criteriaBuilder.equal(root.get("isActive"), isActive);
      }
  }
  ```
  
  4. Repository Güncellemesi:
  ```java
  @Repository
  public interface BranchManagerRepository extends JpaRepository<BranchManager, String>, 
                                                   JpaSpecificationExecutor<BranchManager> {
      // Mevcut metodlar korunacak
  }
  ```
  
  TEST SENARYOLARI:
  - GET /api/branches/123/managers → Tüm yöneticiler
  - GET /api/branches/123/managers?name=ahmet → İsmi "ahmet" içeren yöneticiler  
  - GET /api/branches/123/managers?isActive=true → Sadece aktif yöneticiler
  - GET /api/branches/123/managers?name=ahmet&isActive=false → İsmi "ahmet" içeren pasif yöneticiler
  
  BAŞARI KRİTERİ:
  API, yukarıdaki örneklerdeki gibi query parametrelerini kabul edip
  filtrelenmiş sonuçları döndürebilmelidir.
  
  ====================================================================
*/

// Get branch managers with stats and optional search filters
export const getBranchManagers = async (
  branchId: string,
  searchParams?: BranchManagerSearchParams
): Promise<BranchManagersResponse> => {
  try {
    console.log('Loading managers for branch:', branchId, 'with params:', searchParams);
    
    // GÖREV 1: Backend API'yi çağır (performans için Promise.all kullanılacak)
    const response = await axiosInstance.get<{
      managers: Array<{
        id: string;
        user: {
          id: string;
          name: string; // Backend'den name field'ı geliyor, firstName/lastName yok!
          username: string;
          email?: string;
          authorities: string[]; // Console log'dan gördük
          accountNonExpired: boolean;
          accountNonLocked: boolean;
          credentialsNonExpired: boolean;
          companyId: string | null;
          enabled: boolean;
          password: string;
        };
        assignedAt: string;
        assignedBy: string;
        isActive: boolean;  // FIXED: Backend sends "isActive", not "active"
        notes?: string;
        branch: {
          id: string;
          name: string;
          address: string;
          longitude: number;
          latitude: number;
          averageDailyAttendance: number;
          branchClosingTime: string;
          branchOpeningTime: string;
          dailyEmployeeCount: number;
          isDeleted: boolean;
          phone: string | null;
          status: string;
        };
      }>;
      totalCount: number;
      activeCount: number;
      inactiveCount: number;
    }>(`/api/branches/${branchId}/managers`, {
      params: searchParams
    });

    console.log('Backend managers API success!');
    console.log('Manager response data:', {
      managersCount: response.data.managers.length,
      managers: response.data.managers,
      firstManager: response.data.managers[0], // İlk yöneticinin tam detayı
      managerUserDetails: response.data.managers.map(m => ({
        id: m.id,
        name: m.user?.name, // Backend'den name field'ını kullan
        username: m.user?.username,
        email: m.user?.email,
        fullUserObject: m.user
      })),
      // TEMP: Backend'den gelen tüm user field'ları göster
      backendUserFields: response.data.managers[0]?.user ? Object.keys(response.data.managers[0].user) : [],
      allUserData: response.data.managers.map(m => m.user),
      stats: {
        total: response.data.totalCount,
        active: response.data.activeCount,
        inactive: response.data.inactiveCount
      }
    });

    // Backend'den gelen manager data'sını frontend formatına çevir
    const managers: BranchManager[] = response.data.managers.map(manager => {
      // Backend'den name field'ı geldiği için split ediyoruz
      const fullName = manager.user.name || manager.user.username?.split('@')[0] || 'İsimsiz';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || 'İsimsiz';
      const lastName = nameParts.slice(1).join(' ') || '';

      return {
        id: manager.id,
        assignedAt: manager.assignedAt,
        isActive: manager.isActive, // FIXED: Backend sends 'isActive', not 'active'
        assignedBy: manager.assignedBy,
        notes: manager.notes,
        // User objesi - backend'den name field'ını firstName/lastName'e çeviriyoruz
        user: {
          id: manager.user.id,
          firstName,
          lastName,
          email: manager.user.email || manager.user.username || '',
          phoneNumber: '', // Backend'den gelmiyor, boş bırakıyoruz
          username: manager.user.username,
          roles: manager.user.authorities || [] // Backend'den authorities geliyor
        },
        // Branch objesi - backend'den geliyor
        branch: {
          id: manager.branch.id,
          name: manager.branch.name
        }
      };
    });

    return {
      managers,
      totalCount: response.data.totalCount,
      activeCount: response.data.activeCount,
      inactiveCount: response.data.inactiveCount
    };
  } catch (error: any) {
    console.error('Error loading branch managers:', error);
    console.log('Error details:', {
      status: error.response?.status,
      message: error.message,
      branchId // Fixed object shorthand
    });
    
    // FALLBACK: Use branch's actual responsible users (responsibleUsernames)
    console.log('Backend unavailable, showing branch responsible users as managers...');
    
    try {
      // Get branch details to fetch responsible users
      console.log('Calling getBranchById for branchId:', branchId);
      const branchData = await getBranchById(branchId);
      console.log('Branch data loaded:', {
        branchName: branchData.name,
        branchId: branchData.id,
        responsibleUsernames: branchData.responsibleUsernames,
        responsibleCount: branchData.responsibleUsernames?.length || 0,
        companyName: branchData.companyName,
        fullBranchData: branchData
      });
      
      if (!branchData.responsibleUsernames || branchData.responsibleUsernames.length === 0) {
        console.log('No responsible users found for this branch');
        console.log('Branch responsible users array:', branchData.responsibleUsernames);
        
        // Return empty managers list
        return {
          managers: [],
          totalCount: 0,
          activeCount: 0,
          inactiveCount: 0
        };
      }
      
      // Import AllUsers to get user details
      const { AllUsers } = await import('./api_data');
      const { getUserIdFromToken: getTokenUserId } = await import('../utils/jwt'); // Fixed shadowing
      
      const userId = getTokenUserId();
      if (!userId) {
        throw new Error('User ID not found in token');
      }
      
      console.log('Fetching all users to match responsible usernames...');
      console.log('Current userId:', userId);
      const allUsersData = await AllUsers(userId, 0, 100); // Get more users to find matches
      console.log('AllUsers response:', {
        userCount: allUsersData.length,
        firstUser: allUsersData[0],
        allUsers: allUsersData
      });
      
      // Match responsible usernames with actual user data
      const responsibleManagers: BranchManager[] = [];
      
      branchData.responsibleUsernames.forEach((username, index) => {
        console.log(`Processing responsible user ${index + 1}: "${username}"`);
        
        // Try to find user by email first, then by username
        let matchedUser = allUsersData.find((user: any) => {
          const emailMatch = user.email === username;
          const usernameMatch = user.username === username;
          const constructedEmailMatch = `${user.firstName?.toLowerCase()}.${user.lastName?.toLowerCase()}@firma.com` === username.toLowerCase();
          
          console.log(`Checking user: ${user.firstName} ${user.lastName}`, {
            userEmail: user.email,
            userUsername: user.username,
            targetUsername: username,
            emailMatch,
            usernameMatch,
            constructedEmailMatch
          });
          
          return emailMatch || usernameMatch || constructedEmailMatch;
        });
        
        console.log(`Match result for "${username}":`, matchedUser ? 'FOUND' : 'NOT FOUND');
        
        // If no match found, create a placeholder with the username
        if (!matchedUser) {
          console.log(`Creating placeholder user for: "${username}"`);
          const nameParts = username.replace('@firma.com', '').split('.');
          // Fixed unsafe arithmetic operations
          const firstName = nameParts[0] ? 
            (nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1)) : 'İsim';
          const lastName = nameParts[1] ? 
            (nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1)) : 'Soyisim';
          
          matchedUser = {
            id: `responsible-${index}`,
            firstName,
            lastName,
            email: username,
            username, // Fixed object shorthand
            companyName: branchData.companyName || 'Şirket Adı'
          };
          
          console.log('Placeholder user created:', matchedUser);
        }
        
        const manager: BranchManager = {
          id: `manager-${matchedUser.id}`,
          assignedAt: new Date(Date.now() - (index * 24 * 60 * 60 * 1000)).toISOString(), // Spread over days
          isActive: true, // All responsible users are active by default
          assignedBy: 'admin-user-1',
          notes: `Şube sorumlusu - ${matchedUser.firstName} ${matchedUser.lastName}`,
          user: {
            id: matchedUser.id,
            firstName: matchedUser.firstName || 'İsim',
            lastName: matchedUser.lastName || 'Soyisim',
            email: matchedUser.email || username,
            phoneNumber: matchedUser.phoneNumber || '+90 5XX XXX XXXX',
            profilePictureUrl: undefined,
            roles: ['BRANCH_MANAGER', 'EMPLOYEE'],
            companyName: matchedUser.companyName || branchData.companyName || 'Şirket Adı',
            username: matchedUser.username || username
          },
          branch: {
            id: branchId,
            name: branchData.name
          },
          assignedByUser: {
            firstName: 'System',
            lastName: 'Admin',
            email: 'admin@firma.com'
          }
        };
        
        console.log(`Manager created for "${username}":`, {
          managerName: `${manager.user.firstName} ${manager.user.lastName}`,
          email: manager.user.email,
          isActive: manager.isActive
        });
        
        responsibleManagers.push(manager);
      });

      // Apply search filters to responsible managers
      let filteredManagers = [...responsibleManagers];

      if (searchParams?.name) {
        const searchTerm = searchParams.name.toLowerCase();
        filteredManagers = filteredManagers.filter(manager =>
          manager.user.firstName.toLowerCase().includes(searchTerm) ||
          manager.user.lastName.toLowerCase().includes(searchTerm) ||
          manager.user.email.toLowerCase().includes(searchTerm)
        );
      }

      if (searchParams?.isActive !== undefined) {
        filteredManagers = filteredManagers.filter(manager => 
          manager.isActive === searchParams.isActive
        );
      }

      const responsibleStats = {
        totalCount: responsibleManagers.length,
        activeCount: responsibleManagers.filter(m => m.isActive).length,
        inactiveCount: responsibleManagers.filter(m => !m.isActive).length
      };

      console.log('Branch responsible users converted to managers:', {
        branchName: branchData.name,
        total: filteredManagers.length,
        stats: responsibleStats,
        managersSample: filteredManagers.map(m => `${m.user.firstName} ${m.user.lastName} (${m.user.email})`)
      });

      console.log('FINAL MANAGERS RESULT:', {
        totalManagers: responsibleManagers.length,
        filteredManagers: filteredManagers.length,
        allManagers: responsibleManagers.map(m => ({
          name: `${m.user.firstName} ${m.user.lastName}`,
          email: m.user.email,
          isActive: m.isActive
        })),
        returnData: {
          managers: filteredManagers,
          totalCount: responsibleStats.totalCount,
          activeCount: responsibleStats.activeCount,
          inactiveCount: responsibleStats.inactiveCount
        }
      });

      return {
        managers: filteredManagers,
        totalCount: responsibleStats.totalCount,
        activeCount: responsibleStats.activeCount,
        inactiveCount: responsibleStats.inactiveCount
      };
      
    } catch (branchError) {
      console.error('Could not load branch responsible users:', branchError);
      
      // LAST RESORT: Use general user data as fallback
      console.log('Fallback to general users...');
      
      try {
        // Import AllUsers dynamically to avoid circular dependency
        const { AllUsers } = await import('./api_data');
        const { getUserIdFromToken: getTokenUserId2 } = await import('../utils/jwt'); // Fixed second shadowing
        
        const userId = getTokenUserId2();
        if (!userId) {
          throw new Error('User ID not found in token');
        }
        
        console.log('Fetching users from AllUsers API...');
        const usersData = await AllUsers(userId, 0, 50); // Get first 50 users
        
        // Convert users to BranchManager format with realistic data
        const realManagers: BranchManager[] = usersData.slice(0, 5).map((user: any, index: number) => ({
          id: `manager-${user.id}`,
          assignedAt: new Date(Date.now() - (index * 7 * 24 * 60 * 60 * 1000)).toISOString(), // Spread over weeks
          isActive: index < 3, // First 3 active, rest inactive
          assignedBy: 'admin-user-1',
          notes: `Yönetici - ${user.firstName} ${user.lastName}`,
          user: {
            id: user.id,
            firstName: user.firstName || 'İsim',
            lastName: user.lastName || 'Soyisim', 
            email: user.email || `${user.firstName?.toLowerCase()}.${user.lastName?.toLowerCase()}@firma.com`,
            phoneNumber: user.phoneNumber || '+90 5XX XXX XXXX',
            profilePictureUrl: undefined,
            roles: ['BRANCH_MANAGER', 'EMPLOYEE'],
            companyName: user.companyName || 'Şirket Adı',
            username: user.username || `${user.firstName?.toLowerCase()}.${user.lastName?.toLowerCase()}`
          },
          branch: {
            id: branchId,
            name: 'Merkez Şube'
          },
          assignedByUser: {
            firstName: 'Admin',
            lastName: 'User',
            email: 'admin@firma.com'
          }
        }));

        // Apply search filters to real data
        let filteredManagers = [...realManagers];

        if (searchParams?.name) {
          const searchTerm = searchParams.name.toLowerCase();
          filteredManagers = filteredManagers.filter(manager =>
            manager.user.firstName.toLowerCase().includes(searchTerm) ||
            manager.user.lastName.toLowerCase().includes(searchTerm) ||
            manager.user.email.toLowerCase().includes(searchTerm)
          );
        }

        if (searchParams?.isActive !== undefined) {
          filteredManagers = filteredManagers.filter(manager => 
            manager.isActive === searchParams.isActive
          );
        }

        const realStats = {
          totalCount: realManagers.length,
          activeCount: realManagers.filter(m => m.isActive).length,
          inactiveCount: realManagers.filter(m => !m.isActive).length
        };

        console.log('Real users converted to managers:', {
          total: filteredManagers.length,
          stats: realStats,
          usersSample: filteredManagers.slice(0, 2).map(m => `${m.user.firstName} ${m.user.lastName}`)
        });

        return {
          managers: filteredManagers,
          totalCount: realStats.totalCount,
          activeCount: realStats.activeCount,
          inactiveCount: realStats.inactiveCount
        };
        
      } catch (apiError) {
        console.error('AllUsers API also failed:', apiError);
        
        // FINAL FALLBACK: Static mock data
        console.log('All APIs failed, using static mock data...');
        
        const mockManagers: BranchManager[] = [
          {
            id: 'manager-1',
            assignedAt: '2024-01-15T08:30:00Z',
            isActive: true,
            assignedBy: 'admin-user-1',
            notes: 'Merkez şube yöneticisi',
            user: {
              id: 'user-manager-1',
              firstName: 'Ahmet',
              lastName: 'Yılmaz',
              email: 'ahmet.yilmaz@firma.com',
              phoneNumber: '+90 532 123 4567',
              profilePictureUrl: undefined,
              roles: ['BRANCH_MANAGER', 'EMPLOYEE'],
              companyName: 'Örnek Şirket A.Ş.',
              username: 'ahmet.yilmaz'
            },
            branch: {
              id: branchId,
              name: 'Merkez Şube'
            },
            assignedByUser: {
              firstName: 'Admin',
              lastName: 'User',
              email: 'admin@firma.com'
            }
          }
        ];

        // Apply search filters to mock data
        let filteredManagers = [...mockManagers];

        if (searchParams?.name) {
          const searchTerm = searchParams.name.toLowerCase();
          filteredManagers = filteredManagers.filter(manager =>
            manager.user.firstName.toLowerCase().includes(searchTerm) ||
            manager.user.lastName.toLowerCase().includes(searchTerm) ||
            manager.user.email.toLowerCase().includes(searchTerm)
          );
        }

        if (searchParams?.isActive !== undefined) {
          filteredManagers = filteredManagers.filter(manager => 
            manager.isActive === searchParams.isActive
          );
        }

        const mockStats = {
          totalCount: mockManagers.length,
          activeCount: mockManagers.filter(m => m.isActive).length,
          inactiveCount: mockManagers.filter(m => !m.isActive).length
        };

        console.log('Mock managers data prepared:', {
          total: filteredManagers.length,
          stats: mockStats
        });

        return {
          managers: filteredManagers,
          totalCount: mockStats.totalCount,
          activeCount: mockStats.activeCount,
          inactiveCount: mockStats.inactiveCount
        };
      }
    }
  }
};

/*
  ====================================================================
  GÖREV 5: FRONTEND ENTEGRASYONU TAMAMLANDI 
  ====================================================================
  
  Arama kutusu onChange eventi eklendi
  Durum dropdown onChange eventi eklendi  
  300ms debounce implementasyonu tamamlandı
  Backend API çağrıları search parametreleri ile hazır
  Kullanıcı arama yaptığında getBranchManagers(branchId, searchParams) çağrılıyor
  Console logları ile debug bilgisi eklendi
  
  Frontend Hazır Durumda: 
  - Kullanıcı arama kutusuna yazı yazdığında -> API'ye name parametresi gönderiliyor
  - Kullanıcı durum filtresini değiştirdiğinde -> API'ye isActive parametresi gönderiliyor
  - Her değişiklik sonrası 300ms bekleyip backend'e request atılıyor
  
  Backend Entegrasyonu İçin:
  Görev 4'teki backend implementasyonu tamamlandığında, bu frontend kodu
  otomatik olarak filtrelenmiş sonuçları gösterecektir.
  
  Test Senaryoları (Frontend Hazır):
  Arama kutusu boş -> Tüm yöneticiler
  "ahmet" yazıldığında -> name=ahmet parametresi gönderiliyor
  Durum "Aktif" seçildiğinde -> isActive=true parametresi gönderiliyor  
  Kombinasyon: "ahmet" + "Pasif" -> name=ahmet&isActive=false gönderiliyor
  
  ====================================================================
*/

// Get available users for manager assignment
export const getAvailableManagers = async (branchId: string): Promise<AvailableManagersResponse> => {
  try {
    console.log('Fetching available managers from usersApi.getAll (same as create branch page)...');
    
    // KULLANICI TALEBİ: Create branch sayfasındaki "sorumlu ekle" API'sini kullan
    const { usersApi } = await import('./users.api');
    const response = await usersApi.getAll(0, 50, 'name', 'asc');
    
    console.log('Available managers fetched via usersApi:', {
      count: response.content?.length || 0,
      firstUser: response.content?.[0],
      source: 'usersApi.getAll'
    });

    // UserDto'yu AvailableManager formatına çevir
    const availableManagers = response.content?.map(user => {
      // Backend'den name field'ı geldiği için split ediyoruz
      const fullName = user.name || user.username?.split('@')[0] || 'İsimsiz';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || 'İsimsiz';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return {
        id: user.id,
        firstName,
        lastName,
        email: user.username || '', // UserDto'da email yok, username kullanıyoruz
        phoneNumber: '', // UserDto'da phoneNumber yok
        profilePictureUrl: undefined, // UserDto'da profilePictureUrl yok
        roles: user.role ? [user.role] : [], // UserDto'da roles yok, role var
        companyName: '', // UserDto'da companyName yok
        username: user.username
      };
    }) || [];

    return {
      users: availableManagers // totalCount yok, sadece managers
    };
  } catch (error) {
    console.error('Error loading available managers from usersApi:', error);
    
    // FALLBACK: Try direct API call if usersApi fails
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BRANCHES}/${branchId}/available-managers`);
      return response.data;
    } catch (backendError) {
      console.error('Backend API also failed:', backendError);
      
      // LAST RESORT: Mock data ile yönetici atama modalında gerçek kullanıcılar gösterelim
      console.log('All APIs failed, using mock data...');
      
      const mockAvailableUsers = [
        {
          id: 'user-candidate-1',
          firstName: 'Zeynep',
          lastName: 'Demir',
          email: 'zeynep.demir@firma.com',
          phoneNumber: '+90 535 111 2233',
          profilePictureUrl: undefined,
          roles: ['EMPLOYEE', 'TEAM_LEAD'],
          companyName: 'Örnek Şirket A.Ş.'
        },
        {
          id: 'user-candidate-2',
          firstName: 'Can',
          lastName: 'Arslan',
          email: 'can.arslan@firma.com',
          phoneNumber: '+90 536 444 5566',
          profilePictureUrl: undefined,
          roles: ['EMPLOYEE', 'SENIOR_DEVELOPER'],
          companyName: 'Örnek Şirket A.Ş.'
        },
        {
          id: 'user-candidate-3',
          firstName: 'Selin',
          lastName: 'Çelik',
          email: 'selin.celik@firma.com',
          phoneNumber: '+90 537 777 8899',
          profilePictureUrl: undefined,
          roles: ['EMPLOYEE', 'SUPERVISOR'],
          companyName: 'Örnek Şirket A.Ş.'
        },
        {
          id: 'user-candidate-4',
          firstName: 'Emre',
          lastName: 'Kara',
          email: 'emre.kara@firma.com',
          phoneNumber: '+90 538 999 0011',
          profilePictureUrl: undefined,
          roles: ['EMPLOYEE', 'ANALYST'],
          companyName: 'Örnek Şirket A.Ş.'
        },
        {
          id: 'user-candidate-5',
          firstName: 'Ayşe',
          lastName: 'Şahin',
          email: 'ayse.sahin@firma.com',
          phoneNumber: '+90 539 222 3344',
          profilePictureUrl: undefined,
          roles: ['EMPLOYEE', 'COORDINATOR'],
          companyName: 'Örnek Şirket A.Ş.'
        }
      ];

      console.log('Mock available users prepared:', mockAvailableUsers.length, 'users');

      return {
        users: mockAvailableUsers
      };
    }
  }
};

// Promote an existing employee to branch manager.
// Backend: POST /api/branches/{branchId}/managers/promote  body { employeeId }
// Creates/updates the employee's User to MANAGER, creates the branch_managers row
// and sends an activation email. Works for mobile/web-signup employees without a User.
export const promoteEmployeeToManager = async (branchId: string, employeeId: string): Promise<any> => {
  const response = await axiosInstance.post(`${API_ENDPOINTS.BRANCHES}/${branchId}/managers/promote`, { employeeId });
  return response.data;
};

// Add branch manager
export const addBranchManager = async (data: BranchManagerCreateDto): Promise<BranchManager> => {
  try {
    const response = await axiosInstance.post(`${API_ENDPOINTS.BRANCHES}/${data.branchId}/managers/${data.userId}`);
    return response.data;
  } catch (error) {
    console.error('Error adding branch manager:', error);
    
    // FALLBACK: Mock implementation - simüle edilmiş yönetici ekleme
    console.log('Backend unavailable for adding manager, using mock implementation...');
    
    // Find user from available users mock data
    const mockUser = {
      id: data.userId,
      firstName: 'Yeni',
      lastName: 'Yönetici',
      email: 'yeni.yonetici@firma.com',
      phoneNumber: '+90 540 123 4567',
      profilePictureUrl: undefined,
      roles: ['BRANCH_MANAGER', 'EMPLOYEE'],
      companyName: 'Örnek Şirket A.Ş.',
      username: 'yeni.yonetici'
    };

    const newManager: BranchManager = {
      id: `manager-${Date.now()}`,
      assignedAt: new Date().toISOString(),
      isActive: true,
      assignedBy: data.assignedBy,
      notes: 'Mock atama - Backend hazır olduğunda güncellenecek',
      user: mockUser,
      branch: {
        id: data.branchId,
        name: 'Merkez Şube'
      },
      assignedByUser: {
        firstName: 'Current',
        lastName: 'User',
        email: 'current.user@firma.com'
      }
    };

    console.log('Mock manager assignment completed:', newManager);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return newManager;
  }
};

// Remove branch manager  
export const removeBranchManager = async (branchId: string, userId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`${API_ENDPOINTS.BRANCHES}/${branchId}/managers/${userId}`);
  } catch (error) {
    console.error('Error removing branch manager:', error);
    
    // FALLBACK: Mock implementation - simüle edilmiş yönetici kaldırma
    console.log('Backend unavailable for removing manager, using mock implementation...');
    console.log('Mock manager removal completed for user:', userId);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
  }
};

// Toggle manager active status
export const toggleManagerStatus = async (branchId: string, managerId: string): Promise<BranchManager> => {
  try {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.BRANCHES}/${branchId}/managers/${managerId}/toggle-status`);
    return response.data;
  } catch (error) {
    console.error('Error toggling manager status:', error);
    
    // FALLBACK: Mock implementation - simüle edilmiş durum değişikliği
    console.log('Backend unavailable for toggling status, using mock implementation...');
    
    const mockUpdatedManager: BranchManager = {
      id: managerId,
      assignedAt: '2024-01-15T08:30:00Z',
      isActive: Math.random() > 0.5, // Random aktif/pasif durumu
      assignedBy: 'admin-user-1',
      notes: 'Mock durum değişikliği',
      user: {
        id: 'user-manager-updated',
        firstName: 'Güncellenmiş',
        lastName: 'Yönetici',
        email: 'guncellenmis.yonetici@firma.com',
        phoneNumber: '+90 541 999 8888',
        profilePictureUrl: undefined,
        roles: ['BRANCH_MANAGER', 'EMPLOYEE'],
        companyName: 'Örnek Şirket A.Ş.',
        username: 'guncellenmis.yonetici'
      },
      branch: {
        id: branchId,
        name: 'Merkez Şube'
      },
      assignedByUser: {
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@firma.com'
      }
    };

    console.log('Mock status toggle completed:', mockUpdatedManager);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return mockUpdatedManager;
  }
};

// Mock data generators
const generateMockBranchManagers = (branchId: string): BranchManagersResponse => {
  const managers: BranchManager[] = [
    {
      id: '1',
      assignedAt: '2024-01-15T08:00:00.000Z',
      assignedBy: 'admin-user-1',
      isActive: true,
      notes: 'Initial manager assignment',
      user: {
        id: 'user-manager-1',
        firstName: 'Mehmet',
        lastName: 'Yılmaz',
        email: 'mehmet.yilmaz@firma.com',
        phoneNumber: '+90 532 123 4567',
        profilePictureUrl: '/assets/images/avatar/avatar-1.webp',
        roles: ['MANAGER'],
        companyName: 'ABC Teknoloji',
        username: 'mehmet.yilmaz'
      },
      branch: {
        id: branchId,
        name: 'Branch Name'
      },
      assignedByUser: {
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@firma.com'
      }
    },
    {
      id: '2',
      assignedAt: '2024-02-20T10:30:00.000Z',
      assignedBy: 'admin-user-1',
      isActive: true,
      notes: 'Secondary manager assignment',
      user: {
        id: 'user-manager-2',
        firstName: 'Ayşe',
        lastName: 'Demir',
        email: 'ayse.demir@firma.com',
        phoneNumber: '+90 533 987 6543',
        profilePictureUrl: '/assets/images/avatar/avatar-2.webp',
        roles: ['MANAGER'],
        companyName: 'ABC Teknoloji',
        username: 'ayse.demir'
      },
      branch: {
        id: branchId,
        name: 'Branch Name'
      },
      assignedByUser: {
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@firma.com'
      }
    },
    {
      id: '3',
      assignedAt: '2024-03-10T14:15:00.000Z',
      assignedBy: 'admin-user-1',
      removedAt: '2024-06-01T12:00:00.000Z',
      removedBy: 'admin-user-1',
      isActive: false,
      notes: 'Manager removed due to role change',
      user: {
        id: 'user-manager-3',
        firstName: 'Can',
        lastName: 'Özkan',
        email: 'can.ozkan@firma.com',
        phoneNumber: '+90 534 456 7890',
        profilePictureUrl: '/assets/images/avatar/avatar-3.webp',
        roles: ['MANAGER'],
        companyName: 'ABC Teknoloji',
        username: 'can.ozkan'
      },
      branch: {
        id: branchId,
        name: 'Branch Name'
      },
      assignedByUser: {
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@firma.com'
      }
    }
  ];

  const activeCount = managers.filter(m => m.isActive).length;
  const inactiveCount = managers.filter(m => !m.isActive).length;

  return {
    managers,
    totalCount: managers.length,
    activeCount,
    inactiveCount
  };
};

const generateMockAvailableManagers = (): AvailableManagersResponse => ({
  users: [
    {
      id: 'user-available-1',
      firstName: 'Emre',
      lastName: 'Kaya',
      email: 'emre.kaya@firma.com',
      phoneNumber: '+90 535 111 2233',
      profilePictureUrl: '/assets/images/avatar/avatar-4.webp',
      roles: ['USER'],
      companyName: 'ABC Teknoloji'
    },
    {
      id: 'user-available-2',
      firstName: 'Zehra',
      lastName: 'Şahin',
      email: 'zehra.sahin@firma.com',
      phoneNumber: '+90 536 444 5566',
      profilePictureUrl: '/assets/images/avatar/avatar-5.webp',
      roles: ['USER'],
      companyName: 'ABC Teknoloji'
    },
    {
      id: 'user-available-3',
      firstName: 'Burak',
      lastName: 'Çelik',
      email: 'burak.celik@firma.com',
      phoneNumber: '+90 537 777 8899',
      profilePictureUrl: '/assets/images/avatar/avatar-6.webp',
      roles: ['USER'],
      companyName: 'ABC Teknoloji'
    }
  ]
});
